import { create } from "zustand";

/**
 * Map onboarding-flow state, extracted from MapCanvas.tsx (Phase 2 of the
 * MapCanvas clean-architecture redesign — see plan/map-architecture/).
 *
 * Scope: only the onboarding-sequence flags that gate/drive the first-visit
 * flow (zoom-to-plot animation, "build your first house" popup, first-visit
 * zoom hint). Modal *visibility* itself lives in useMapModalStore
 * (activeModal === 'mapOnboarding' | 'tutorialReward' | 'mapWelcome' etc.);
 * this store only holds the flags that don't fit that discriminated union
 * (a step counter, boolean interaction-blockers, a tri-state hint phase).
 */
export type FirstVisitPhase = 'zoom_hint' | null;

interface MapOnboardingStore {
    /** Current step index reported by MapOnboardingModal's onStepChange. */
    onboardingStep: number;
    setOnboardingStep: (step: number) => void;
    /** True while the forced zoom-to-plot/cityhall animation is blocking interaction. */
    isOnboardingZooming: boolean;
    setIsOnboardingZooming: (value: boolean) => void;
    /** "Build your first house" popup shown after the map tutorial modal closes. */
    isOnboardingBuildHousePopupOpen: boolean;
    setIsOnboardingBuildHousePopupOpen: (value: boolean) => void;
    /** Inside the build-house popup: no free plots found, show fallback message. */
    onboardingNoPlotsMessage: boolean;
    setOnboardingNoPlotsMessage: (value: boolean) => void;
    /** First-ever map visit: zoom-hint → welcome-modal sequence. */
    firstVisitPhase: FirstVisitPhase;
    setFirstVisitPhase: (phase: FirstVisitPhase) => void;
}

const useMapOnboardingStore = create<MapOnboardingStore>((set) => ({
    onboardingStep: 0,
    setOnboardingStep: (step) => set({ onboardingStep: step }),
    isOnboardingZooming: false,
    setIsOnboardingZooming: (value) => set({ isOnboardingZooming: value }),
    isOnboardingBuildHousePopupOpen: false,
    setIsOnboardingBuildHousePopupOpen: (value) => set({ isOnboardingBuildHousePopupOpen: value }),
    onboardingNoPlotsMessage: false,
    setOnboardingNoPlotsMessage: (value) => set({ onboardingNoPlotsMessage: value }),
    firstVisitPhase: null,
    setFirstVisitPhase: (phase) => set({ firstVisitPhase: phase }),
}));

export default useMapOnboardingStore;
